import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { AttributionControl, Map } from 'react-map-gl/mapbox';
import { MapboxOverlay as DeckOverlay } from '@deck.gl/mapbox';
import type { MapRef } from './types';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  TextareaAutosize,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { MapConfig, MapStateDef } from './Energy/MapTypes';
import { MAP_CONFIG_DEMO1 } from './Energy/MapConfigDemo1';
import { spoxmenLocation, enexisLocation } from './Energy/DemoConstants';
import { MVTLayerManager, MVTLayerConfig } from './Energy/MVTLayerManager';
import { BitmapLayerManager } from './Energy/BitmapLayerManager';

const mapConfig : MapConfig = MAP_CONFIG_DEMO1;

const deckOverlay = new DeckOverlay({
  layers: [],
  interleaved: true
});

const EnergyMap = React.memo(Map);

interface EnergyProps {
  accessToken: string;
}

function Energy({ accessToken }: EnergyProps) {
  const logoRadius = 0.0004;

  const logoLayer = useMemo(
    () => {
      console.log('Creating logo layer');
      const logoLayerManager = BitmapLayerManager.createFromCenterAndRadius({
        id: 'LogoLayer',
        imageUrl: 'images/logo.png',
        center: spoxmenLocation,
        radius: logoRadius,
      });
      return logoLayerManager;
    },
    []
  );

  const enexisLayer = useMemo(
    () => {
      console.log('Creating enexis layer');
      const enexisLayerManager = BitmapLayerManager.createFromCenterAndRadius({
        id: 'EnexisLayer',
        imageUrl: 'images/enexis.png',
        center: enexisLocation,
        radius: logoRadius,
      });
      return enexisLayerManager;
    },
    []
  );

const mvtLayer = useMemo(
  () => {
    console.log('Creating MVT layer');
    const layerUrl: string = `https://api.mapbox.com/v4/spoxmen.77uxahbv/{z}/{x}/{y}.vector.pbf?access_token=${accessToken}`;   

    const mvtLayerConfig: MVTLayerConfig = {
      id: 'MVTLayer',
      layerUrl: layerUrl,
      minZoom: 0,
      maxZoom: 14,
      elevation: 50,
      lineColor: [255, 200, 200, 200],
      pointColor: [255, 0, 0, 255],
      lineWidth: 10,
      pointRadius: 50,
      visible: true
    };

    const mvtLayerManager = new MVTLayerManager(mvtLayerConfig);
    return mvtLayerManager;
  }, [accessToken]
  );

  const deckGLLayersRef = useRef<(BitmapLayerManager | MVTLayerManager)[]>([logoLayer, enexisLayer, mvtLayer]);

  const mapRef = useRef<MapRef>(null);

  const [mapState, setMapState] = useState<string>(mapConfig.initialMapState);
  const [selectedStatnaam, setSelectedStatnaam] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [autoAdvancePaused, setAutoAdvancePaused] = useState<boolean>(true);

  const currentDescription = mapConfig.mapStates[mapState]?.description || '';

  const latestMapStateRef = useRef<string>(mapState);
  // store timeout id for auto-advance so we can clear it when necessary
  const autoAdvanceTimeoutRef = useRef<number | null>(null);
  const autoAdvancePausedRef = useRef<boolean>(true);

  // use the global map style defined in mapConfig if present
  const currentMapStyle = mapConfig.mapStyle;

  const setLayerVisibility = useCallback((stateName: string) => {
    const stateObj : MapStateDef = mapConfig.mapStates[stateName];
      if (!stateObj) return;
      setMapboxLayerVisibility(stateObj.visibleLayers);
      setDeckGLLayerVisibility(stateObj.visibleDeckGLLayers);
  }, []);

  const applyMapState = useCallback((stateName: string) => {
    latestMapStateRef.current = stateName;
    const stateObj : MapStateDef = mapConfig.mapStates[stateName];
    if (!stateObj) return;
    // clear any existing auto-advance timer
    if (autoAdvanceTimeoutRef.current) {
      window.clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    const vs = stateObj.mapState;
    const map = mapRef.current?.getMap?.();
    if (map) {
      map.flyTo({
        center: [vs.longitude, vs.latitude],
        zoom: vs.zoom,
        bearing: vs.bearing,
        pitch: vs.pitch,
        essential: true,
        duration: stateObj.transitionDuration ?? 0
      });
      setTimeout(() => {
        if (latestMapStateRef.current === stateName) {
          setLayerVisibility(stateName);
        }
      }, stateObj.layerVisibilityDelay || 0);

      // do not schedule auto-advance if paused
      if (!autoAdvancePausedRef.current) {
        // schedule auto-advance if configured
        const ms = stateObj.autoAdvanceMs;
        if (ms && ms > 0) {
          autoAdvanceTimeoutRef.current = window.setTimeout(() => {
            // ensure we are still on the same state before advancing
            if (latestMapStateRef.current === stateName) {
              const idx = mapConfig.mapSequence.indexOf(stateName);
              const nextIdx = (idx + 1) % mapConfig.mapSequence.length;
              const target = mapConfig.mapSequence[nextIdx];
              setMapState(target);
              applyMapState(target);
            }
          }, ms) as unknown as number;
        }
      }
    }
  }, [setLayerVisibility]);

  // clear auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) {
        window.clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };
  }, []);

  const handlePrev = useCallback(() => {
    const idx = mapConfig.mapSequence.indexOf(mapState);
    const prevIdx = (idx - 1 + mapConfig.mapSequence.length) % mapConfig.mapSequence.length;
    const target = mapConfig.mapSequence[prevIdx];
    setMapState(target);
    applyMapState(target);
  }, [mapState, applyMapState]);

  const handleNext = useCallback(() => {
    const idx = mapConfig.mapSequence.indexOf(mapState);
    const nextIdx = (idx + 1) % mapConfig.mapSequence.length;
    const target = mapConfig.mapSequence[nextIdx];
    setMapState(target);
    applyMapState(target);
  }, [mapState, applyMapState]);

  const toggleAutoAdvancePaused = useCallback(() => {
    const newVal = !autoAdvancePausedRef.current;
    autoAdvancePausedRef.current = newVal;
    setAutoAdvancePaused(newVal);
    // if pausing, clear any scheduled auto-advance immediately
    if (newVal && autoAdvanceTimeoutRef.current) {
      window.clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    } else {
      // if resuming, trigger next to resume progression
      handleNext();
    }
  }, [handleNext]);

  function setMapboxLayerVisibility(visibleLayerNames: string[]) {
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    mapConfig.switchableLayers.forEach((layerId: string) => {
      if (!map.getLayer(layerId)) return;

      const visible = visibleLayerNames.includes(layerId);

      map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
    });
  }

  function setDeckGLLayerVisibility(visibleLayerNames: string[]) {
    const currentLayers = deckGLLayersRef.current;
    if (!currentLayers) return;

    for (const layer of currentLayers) {
      layer.setVisibility(visibleLayerNames.includes(layer.getId()));
    }
    const newLayers = currentLayers.map((lyr) => lyr.getLayer());
    deckOverlay.setProps({ layers: newLayers });
  }

  interface DeckClickInfo {
    layer?: { id?: string };
    object?: { properties?: { statnaam?: string } };
  }

  const handleDeckClick = useCallback((info: DeckClickInfo) => {
    if (info?.layer?.id === 'MVTLayer' && info?.object?.properties?.statnaam) {
      const feature = info.object;
      const statnaam = feature?.properties?.statnaam;
      if (statnaam) setSelectedStatnaam(statnaam);
    }
  }, []);

  const onMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    (map as any).accessToken = accessToken; // ensure token present
    map.setTerrain();

    const layersToSet = deckGLLayersRef.current.map(layer => layer.getLayer()) ?? undefined;
    deckOverlay.setProps({
      layers: layersToSet,
      onClick: handleDeckClick as any
    });

    if (!(map as any)._deckOverlayAdded) {
      map.addControl(deckOverlay);
      (map as any)._deckOverlayAdded = true;
    }

    mapConfig.switchableLayers.forEach((layerId: string) => {
      setMapboxLayerVisibility([]); // hide by default
    });

  applyMapState(mapState);
  }, [applyMapState, mapState, accessToken, handleDeckClick]);

  return (
    <Box sx={{ height: '100vh', width: '100vw', overflow: 'hidden', bgcolor: 'background.default', color: 'text.primary' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1400,
            pointerEvents: 'none'
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(0,0,0,0.6)',
              px: 3,
              py: 1,
              borderRadius: 2,
              maxWidth: 600
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', textAlign: 'center', fontWeight: 400, fontSize: '1.1rem' }}>
              {currentDescription}
            </Typography>
          </Box>
        </Box>
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          variant="temporary"
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary'
            }
          }}
        >
          <Box sx={{ width: 300, pt: 2 }}>
            <List>
              <ListItem>
                <ListItemText primary="Navigation" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Selection" />
              </ListItem>
              <ListItem>
                <Typography variant="body2">
                  Selected: <strong>{selectedStatnaam || '—'}</strong>
                </Typography>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Description" />
              </ListItem>
              <ListItem>
                <TextareaAutosize
                  minRows={8}
                  value={currentDescription}
                  readOnly
                  style={{
                    width: '100%',
                    fontSize: '1rem',
                    background: '#232323',
                    color: '#fff',
                    border: '1px solid #444',
                    padding: '0.5rem'
                  }}
                />
              </ListItem>
            </List>
          </Box>
        </Drawer>
        <Box id="main-content" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100vh', bgcolor: 'background.default' }}>
          <EnergyMap
            key={'EnergyMap'}
            ref={mapRef as any}
            onLoad={onMapLoad}
            attributionControl={false}
            mapStyle={currentMapStyle}
            mapboxAccessToken={accessToken}
            style={{ width: '100%', height: '100%' }}
            reuseMaps
          >
            <AttributionControl key="EnergyMapAttributionControl" position="bottom-right" />
          </EnergyMap>
        </Box>
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            bottom: 128,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1401,
            pointerEvents: 'none'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              bgcolor: 'rgba(0,0,0,0.7)',
              p: 1,
              borderRadius: 2,
              boxShadow: 3,
              pointerEvents: 'auto'
            }}
          >
            <Button variant="outlined" color="primary" onClick={handlePrev} sx={{ color: '#bdbdbd', borderColor: '#bdbdbd' }}>
              Prev
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={toggleAutoAdvancePaused}
              sx={{ color: '#bdbdbd', borderColor: '#bdbdbd' }}
            >
              {autoAdvancePaused ? 'Resume' : 'Pause'}
            </Button>
            <Button variant="outlined" color="primary" onClick={handleNext} sx={{ color: '#bdbdbd', borderColor: '#bdbdbd' }}>
              Next
            </Button>
          </Box>
        </Box>
        <AppBar
          position="fixed"
            color="primary"
            sx={{
              top: 'auto',
              bottom: 32,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(6px)',
              boxShadow: 3
            }}
        >
          <Toolbar>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setDrawerOpen(true)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Spoxmen
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="outlined" color="primary" sx={{ color: '#bdbdbd', borderColor: '#bdbdbd' }}>
                Open your story
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      </Box>
  );
}

export default Energy;
